import { produce } from 'immer'
import { useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { To, useNavigate, useSearchParams } from 'react-router-dom'

import { Button, ButtonGroup, Divider, FormGroup, InputGroup, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import useAuth from '../../aws/useAuth'
import { useRootOutletContext } from '../root/Root'
import styles from './SignIn.module.scss'

export default (props: {
    redirectWhenAuthenticated: (currentSearchParams: URLSearchParams) => To,
    signUpLink: (currentSearchParams: URLSearchParams) => To,
    resetPasswordLink: (currentSearchParams: URLSearchParams) => To,
}) => {
    const auth = useAuth()
    const [searchParams,] = useSearchParams()
    const { toaster } = useRootOutletContext()
    const [state, setState] = useState({
        username: searchParams.get("username") || "",
        password: "",
    })

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { executeRecaptcha } = useGoogleReCaptcha()

    // if auth state is changed, let user navigate to next page
    useEffect(() => {
        if (auth.isLoaded && auth.isAuthenticated)
            navigate(props.redirectWhenAuthenticated(searchParams))
    }, [auth, navigate, searchParams, props])

    const executeSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const token = await executeRecaptcha?.("signIn")
        if (!token) {
            toaster?.show({ message: t('toast.recaptchaFailed.message'), icon: IconNames.Error, intent: Intent.DANGER })
            return
        }

        const result = await auth.signIn(state.username, state.password, { recaptcha_token: token })

        if (result.success) toaster?.show({ message: t('toast.signInSuccess.message'), icon: IconNames.Tick, intent: Intent.SUCCESS })
        else toaster?.show({ message: t('toast.wrongUsernamePassword.message'), icon: IconNames.Error, intent: Intent.DANGER })
    }

    return <>
        <h1
            className={styles.title}
        >{t('signIn.title')}</h1>
        <form onSubmit={executeSignIn}>
            <FormGroup label={t('signIn.form.usernameInput.label')} labelFor="username" labelInfo={t('common.requiredInput.labelInfo')}>
                <InputGroup
                    id="username"
                    leftIcon="envelope"
                    type="email"
                    autoComplete="email"
                    value={state.username}
                    onChange={e => setState(produce(draft => {
                        draft.username = e.target.value
                    }))}
                    required />
            </FormGroup>
            <FormGroup label={t('signIn.form.passwordInput.label')} labelFor="password" labelInfo={t('common.requiredInput.labelInfo')}>
                <InputGroup
                    id="password"
                    leftIcon="lock"
                    type="password"
                    autoComplete="current-password"
                    value={state.password}
                    onChange={e => setState(produce(draft => {
                        draft.password = e.target.value
                    }))}
                    required
                />
            </FormGroup>
            <Button
                className={styles.forgotPasswordButton}
                text={t('signIn.forgotPasswordButton.label')}
                intent={Intent.DANGER}
                onClick={() => navigate(props.resetPasswordLink(searchParams))}
                minimal
            />
            <Button
                className={styles.submitButton}
                intent={Intent.PRIMARY}
                type="submit"
                text={t('signIn.form.submitButton.label')} />
        </form>

        <Divider className={styles.divider} />
        <ButtonGroup
            className={styles.footerButtonGroup}
            vertical
            minimal>
            <Button
                text={t('signIn.navigateToSignUpButton.label')}
                rightIcon="arrow-right"
                onClick={() => navigate(props.signUpLink(searchParams))}
                className={styles.divider}
            />
        </ButtonGroup>
    </>
}
